import React, { useState } from 'react';

interface NotificationModalProps {
    onClose: () => void;
    onSend: (data: {
        message: string;
        channels: ('telegram' | 'push' | 'email')[];
        type: 'maintenance' | 'force-reload';
        title?: string;
    }) => Promise<void>;
    progress: 'idle' | 'sending' | 'success';
}

export const NotificationModal: React.FC<NotificationModalProps> = ({ onClose, onSend, progress }) => {
    const [message, setMessage] = useState('');
    const [type, setType] = useState<'maintenance' | 'force-reload'>('maintenance');
    const [channels, setChannels] = useState<('telegram' | 'push' | 'email')[]>(['telegram', 'push']);
    const [title, setTitle] = useState('');

    React.useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [onClose]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (type === 'maintenance' && !message) {
            alert('Bitte Nachricht eingeben');
            return;
        }
        onSend({ message, channels, type, title: title || undefined });
    };

    const toggleChannel = (channel: 'telegram' | 'push' | 'email') => {
        if (channels.includes(channel)) {
            setChannels(channels.filter(c => c !== channel));
        } else {
            setChannels([...channels, channel]);
        }
    };

    return (
        <div className="admin-modal-overlay">
            <div className="admin-notification-modal-content">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <h3 style={{ margin: 0, fontSize: '20px', color: 'var(--text-main)' }}>Globale Benachrichtigung</h3>
                    <button onClick={onClose} className="admin-modal-close">✕</button>
                </div>

                {progress === 'success' ? (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
                        <h4 style={{ margin: '0 0 8px 0', color: 'var(--text-main)' }}>Gesendet!</h4>
                        <p style={{ color: 'var(--text-muted)' }}>Die Benachrichtigung wurde in die Warteschlange gestellt.</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <div style={{ marginBottom: '20px' }}>
                            <label className="admin-label">Typ</label>
                            <div className="admin-type-tabs">
                                <button
                                    type="button"
                                    onClick={() => setType('maintenance')}
                                    className={`admin-type-tab ${type === 'maintenance' ? 'active-maintenance' : ''}`}
                                >
                                    Wartung / Info
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setType('force-reload');
                                        setChannels(['push']);
                                    }}
                                    className={`admin-type-tab ${type === 'force-reload' ? 'active-reload' : ''}`}
                                >
                                    Force Reload
                                </button>
                            </div>
                        </div>

                        {type === 'maintenance' && (
                            <div style={{ marginBottom: '20px' }}>
                                <label className="admin-label">Titel (Optional)</label>
                                <input
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="Wartungsarbeiten angekündigt"
                                    className="admin-input"
                                />
                            </div>
                        )}

                        <div style={{ marginBottom: '20px' }}>
                            <label className="admin-label">
                                {type === 'maintenance' ? 'Nachricht' : 'Reload-Hinweis (Interner Push)'}
                            </label>
                            <textarea
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                placeholder={type === 'maintenance' ? 'Das System wird heute um 20:00 Uhr kurz gewartet...' : 'App wird aktualisiert...'}
                                className="admin-input"
                                style={{ minHeight: '100px', resize: 'vertical' }}
                            />
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                            <label className="admin-label">Kanäle</label>
                            <div className="admin-channel-group">
                                {(['push', 'telegram', 'email'] as const).map(c => {
                                    const isDisabled = type === 'force-reload' && c !== 'push';
                                    return (
                                        <label key={c} className={`admin-channel-item ${isDisabled ? 'disabled' : ''}`}>
                                            <input
                                                type="checkbox"
                                                checked={channels.includes(c)}
                                                disabled={isDisabled}
                                                onChange={() => toggleChannel(c)}
                                            />
                                            <span style={{ fontSize: '14px', textTransform: 'capitalize' }}>{c}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={progress === 'sending' || channels.length === 0}
                            className={`admin-submit-btn ${type === 'maintenance' ? 'maintenance' : 'reload'}`}
                        >
                            {progress === 'sending' ? 'Wird gesendet...' : 'Jetzt senden'}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};
